import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';

@Component({
  standalone: false,
  selector: 'app-info-hint',
  templateUrl: './info-hint.component.html',
  styleUrls: ['./info-hint.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfoHintComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly DESKTOP_CLOSE_DELAY = 300;
  private static readonly VIEWPORT_MARGIN = 8;
  private static readonly PANEL_OFFSET = 10;
  private static readonly VERTICAL_ARROW_HEIGHT = 14;
  private static readonly HORIZONTAL_ARROW_WIDTH = 12;

  @Input() text = '';
  @Input() position: 'top' | 'right' | 'bottom' | 'left' = 'top';
  @Input() iconSrc = '';
  @Input() ariaLabel = 'Show tooltip';
  @Input() rtl = false;

  @ContentChild(TemplateRef) contentTemplate: TemplateRef<any> | null = null;
  @ViewChild('panelRef') panelRef!: ElementRef<HTMLElement>;
  @ViewChild('triggerRef') triggerRef!: ElementRef<HTMLElement>;

  @HostBinding('class.info-hint-host')
  hostClass = true;

  isOpen = false;
  isTouchMode = false;
  panelTop = 0;
  panelLeft = 0;
  arrowLeft = 0;
  arrowTop = 0;
  arrowLeftCss = '0px';
  arrowTopCss = '0px';

  private closeTimeoutId: number | null = null;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly renderer: Renderer2
  ) {
  }

  ngOnInit(): void {
    this.isTouchMode = this.detectTouchMode();
  }

  ngAfterViewInit(): void {
    this.renderer.appendChild(document.body, this.panelRef.nativeElement);
    this.syncPanelPosition();
  }

  ngOnDestroy(): void {
    this.clearCloseTimeout();
    if (this.panelRef && this.panelRef.nativeElement.parentNode) {
      this.panelRef.nativeElement.parentNode.removeChild(this.panelRef.nativeElement);
    }
  }

  onMouseEnter(): void {
    if (this.isTouchMode) {
      return;
    }

    this.clearCloseTimeout();
    this.open();
  }

  onMouseLeave(): void {
    if (this.isTouchMode) {
      return;
    }

    this.scheduleClose();
  }

  onFocus(): void {
    if (this.isTouchMode) {
      return;
    }

    this.open();
  }

  onBlur(): void {
    if (this.isTouchMode) {
      return;
    }

    this.close();
  }

  onTriggerClick(event: Event): void {
    event.stopPropagation();

    if (!this.isTouchMode) {
      return;
    }

    event.preventDefault();

    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const nextTouchMode = this.detectTouchMode();

    if (nextTouchMode !== this.isTouchMode) {
      this.isTouchMode = nextTouchMode;
      this.close();
      return;
    }

    if (this.isOpen) {
      this.syncPanelPosition();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.isOpen) {
      return;
    }

    this.syncPanelPosition();
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();

    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  onPanelMouseEnter(): void {
    if (this.isTouchMode) {
      return;
    }

    this.clearCloseTimeout();
  }

  onPanelMouseLeave(): void {
    if (this.isTouchMode) {
      return;
    }

    this.scheduleClose();
  }

  close(): void {
    this.clearCloseTimeout();

    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.changeDetectorRef.markForCheck();
  }

  private open(): void {
    this.clearCloseTimeout();
    this.isOpen = true;
    this.syncPanelPosition();
    this.changeDetectorRef.markForCheck();
  }

  private detectTouchMode(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  private scheduleClose(): void {
    this.clearCloseTimeout();

    this.closeTimeoutId = window.setTimeout(() => {
      this.close();
    }, InfoHintComponent.DESKTOP_CLOSE_DELAY);
  }

  private clearCloseTimeout(): void {
    if (this.closeTimeoutId === null) {
      return;
    }

    window.clearTimeout(this.closeTimeoutId);
    this.closeTimeoutId = null;
  }

  private syncPanelPosition(): void {
    if (!this.panelRef || !this.triggerRef) {
      return;
    }

    const triggerRect = this.triggerRef.nativeElement.getBoundingClientRect();
    const panelRect = this.panelRef.nativeElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const minLeft = InfoHintComponent.VIEWPORT_MARGIN;
    const maxLeft = Math.max(minLeft, viewportWidth - panelRect.width - InfoHintComponent.VIEWPORT_MARGIN);
    const minTop = InfoHintComponent.VIEWPORT_MARGIN;
    const maxTop = Math.max(minTop, viewportHeight - panelRect.height - InfoHintComponent.VIEWPORT_MARGIN);
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;

    if (this.position === 'top' || this.position === 'bottom') {
      const top = this.position === 'top'
        ? triggerRect.top - panelRect.height - InfoHintComponent.PANEL_OFFSET - InfoHintComponent.VERTICAL_ARROW_HEIGHT
        : triggerRect.bottom + InfoHintComponent.PANEL_OFFSET + InfoHintComponent.VERTICAL_ARROW_HEIGHT;
      const left = this.clamp(
        triggerCenterX - panelRect.width / 2,
        minLeft,
        maxLeft
      );

      this.panelTop = this.clamp(top, minTop, maxTop);
      this.panelLeft = left;
      this.arrowLeft = this.clamp(triggerCenterX - left, 20, panelRect.width - 20);
      this.arrowLeftCss = this.arrowLeft + 'px';
      return;
    }

    const top = this.clamp(
      triggerCenterY - panelRect.height / 2,
      minTop,
      maxTop
    );
    const left = this.position === 'left'
      ? triggerRect.left - panelRect.width - InfoHintComponent.PANEL_OFFSET - InfoHintComponent.HORIZONTAL_ARROW_WIDTH
      : triggerRect.right + InfoHintComponent.PANEL_OFFSET + InfoHintComponent.HORIZONTAL_ARROW_WIDTH;

    this.panelTop = top;
    this.panelLeft = this.clamp(left, minLeft, maxLeft);
    this.arrowTop = this.clamp(triggerCenterY - top, 20, panelRect.height - 20);
    this.arrowTopCss = this.arrowTop + 'px';
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
